<script setup lang="ts">
import type { GradeCode } from '~/models/Grade'
import type { ContentProductHeader } from '~/models/Content/ContentProductHeader'
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { colors, getColorForId } from '~/utils/colors'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'
import StackedCard from '~/components/cards/StackedCard.vue'
import SearchCard from '~/components/cards/SearchCard.vue'

const { exam, selectedGrades } = defineProps<{
  exam: ContentProductHeader
  selectedGrades: GradeCode[]
}>()

const { findContents } = useContentApi()

const { data: groupedResources, isLoading: isLoadingResources } = useQuery({
  queryKey: computed(() => ['old_exams_resources', exam.locationId, selectedGrades]),
  queryFn: async () => await findContents<BaseItem>({
    parentLocationIdCriterion: [exam.locationId],
    contentTypeCriterion: [ContentType.ProductPackage, ContentType.LearningPath],
    gradeFieldCriterion: selectedGrades,
    gradeOperator: 'or',
    sortField: 'priority',
    sortOrder: 'asc',
  }),
  select: resources => groupResourcesByLocationId(exam, resources),
  enabled: computed(() => !!exam),
  staleTime: Infinity,
})

function groupResourcesByLocationId(
  exam: ContentProductHeader,
  resources: BaseItem[],
) {
  return {
    header: exam,
    resources: resources.filter(resource => resource.parentLocationId === exam.locationId),
  }
}
</script>

<template>
  <ul
    v-if="!isLoadingResources && groupedResources"
    class="v-if= grid grid-cols-1 gap-4 xs:grid-cols-2 lg:grid-cols-3"
  >
    <StackedCard
      v-for="resource in groupedResources.resources"
      :key="resource.locationId"
      :class="resource.thumbnail ? 'row-span-2' : 'row-span-1'"
      :item="resource"
      class="size-full"
      element="div"
      padded-stack
      :color="getColorForId(resource.parentLocationId, colors)"
    >
      <template #default="{ item }">
        <SearchCard
          v-if="item"
          :item="item"
          package-style
        />
      </template>
    </StackedCard>
  </ul>
</template>
